import React, { useEffect, useRef, useState } from "react";
import { Modal, ModalDialog, ModalClose, Button, Select, Option, LinearProgress, ToggleButtonGroup, CircularProgress } from "@mui/joy";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getFetch, postFetch } from "../../../../fetch/fetch";
import { englishLocationToKorean } from "../../../../data/locationFuntion";

//지문 등록 모달 (학생정보 > 전체 학생정보 > 인증 지문 보기)
//- 네트워크 장치(BioLite 등): CRM 서버 경유 스캔/검증 (/dashboard/biostarenroll/fingerprint/*)
//- USB 등록기(BioMini): 이 PC의 USB Device Agent(localhost:8081)를 브라우저가 직접 호출 → 저장만 CRM 서버
//- 같은 손가락 2회 스캔 → 일치 검증 → 등록. 등록된 지문 삭제 기능은 없음 (BioStar에서만)
//- 장치 드롭다운 기본값은 관리자 설정(본사직원페이지 > 지점별 인증 등록장치 설정)의 로그인 선생님 지점 기준

const toastOptions: any = { position: "bottom-right", autoClose: 2500, hideProgressBar: true, theme: "colored" };
const notifySuccess = (message: string) => toast.success(message, toastOptions);
const notifyError = (message: string) => toast.error(message, toastOptions);

const API = "/dashboard/biostarenroll";
const USB_VALUE = "usb";
//USB 등록기(BioMini) 에이전트는 이 PC에서 도는 로컬 프로세스라 peetsunbae.com 전용 래퍼(getFetch/postFetch)를 쓸 수 없음.
//이 파일에서만 예외적으로 fetch를 직접 사용한다. (https 자체서명 인증서가 신뢰되지 않으면 http 8082로 폴백)
const USB_AGENT_BASES = ["https://localhost:8081", "http://localhost:8082"];
const USB_TIMEOUT_MS = 60000;          //에이전트 팝업에서 Cancel 누를 때까지 대기하므로 넉넉히
const NETWORK_SCAN_SECONDS = 10;       //장치 scan_timeout 과 동일
const MAX_CONSECUTIVE_FAIL = 3;        //두 번째 스캔 연속 실패 허용 횟수 (넘으면 처음부터)
const QUALITY_OPTIONS = [40, 60, 80, 100];

type Phase = "idle" | "scanning1" | "done1" | "error1" | "scanning2" | "verifying" | "done2" | "error2" | "mismatch" | "enrolling";

interface ScanResult {
    template: string;
    quality: number | null;
    image: string | null;   //jpeg base64
}

interface ScanError {
    message: string;
    code: string;
}

type ScanOutcome = { ok: true; data: ScanResult } | { ok: false; error: ScanError };

interface DeviceOption {
    value: string;               //"usb" | BioStar device id
    id: string | null;
    kind: "usb" | "network";
    label: string;
    online: boolean;             //usb 는 항상 true
    location: string | null;     //english 지점코드
    isDefault: boolean;
    group: string;               //"USB" | "내 지점" | 지점 한글명 | "기타"
}

interface FingerprintRecognitionModalProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    selectedUserId: any;                     //users.id
    selectedUserFingerprintId: any;          //BioStar user_id (헤더 fallback)
    selectedUserName: any;
    selectedUserLocation: string | null;     //학생 지점(한글) 헤더 fallback
    onEnrolled: (fingerprintCount: number) => void;
    user: any;
}

//서버 error 문자열 → 화면 메시지 (code 는 개발자 참고용 작은 글씨)
const mapServerScanError = (error: any): ScanError => {
    switch (error) {
        case "timeout": return { message: "10초 안에 지문이 인식되지 않았습니다.", code: "65231" };
        case "quality": return { message: "지문 품질이 낮습니다. 손가락을 다시 올려주세요.", code: "65228" };
        case "device_busy": return { message: "장치가 다른 등록 작업 중입니다. 잠시 후 다시 시도해주세요.", code: "device_busy" };
        case "device_offline": return { message: "장치가 오프라인입니다. 다른 장치를 선택하거나 관리자에게 문의하세요.", code: "device_offline" };
        case "biostar_error": return { message: "BioStar 서버와 통신에 실패했습니다. 잠시 후 다시 시도해주세요.", code: "biostar_error" };
        default: return { message: "장치 오류가 발생했습니다. 잠시 후 다시 시도해주세요.", code: error ? String(error) : "device_error" };
    }
};

const AGENT_UNREACHABLE: ScanError = { message: "USB 등록기 에이전트에 연결할 수 없습니다. 에이전트 실행 여부를 확인해주세요.", code: "agent_unreachable" };
const AGENT_TIMEOUT: ScanError = { message: "USB 등록기 응답이 없습니다(60초). 에이전트 창을 확인해주세요.", code: "agent_timeout" };
const SERVER_UNREACHABLE: ScanError = { message: "서버와 통신에 실패했습니다. 네트워크를 확인해주세요.", code: "network" };
const VERIFY_FAILED: ScanError = { message: "지문 대조에 실패했습니다. 두 번째 스캔을 다시 해주세요.", code: "verify_error" };
const EMPTY_TEMPLATE: ScanError = { message: "스캔 결과가 비어 있습니다. 다시 스캔해주세요.", code: "empty_template" };

//기본 장치 설정: 선생님 지점 설정 우선. 운영진처럼 지점이 없는 계정은 학생 지점 설정으로 폴백
const resolveDefaultSetting = (info: any): { setting: any; location: string | null } => {

    if (!info || !info.settings) {
        return { setting: null, location: null };
    }

    const teacherLocation = info.teacherLocation;

    if (teacherLocation && teacherLocation !== "total") {
        const setting = info.settings[teacherLocation];
        return { setting: setting && setting.fingerprintMode ? setting : null, location: teacherLocation };
    }

    const studentLocation = info.student ? info.student.locationEnglish : null;

    if (studentLocation && info.settings[studentLocation] && info.settings[studentLocation].fingerprintMode) {
        return { setting: info.settings[studentLocation], location: studentLocation };
    }

    return { setting: null, location: null };

};

//드롭다운 항목: USB 등록기(항상 맨 위) + 지문 장치(내 지점 → 지점명 → 기타)
const buildDeviceOptions = (info: any, teacherLocation: string | null, defaultSetting: any): DeviceOption[] => {

    const usbDefault = !!(defaultSetting && defaultSetting.fingerprintMode === "usb");
    const defaultDeviceId = defaultSetting && defaultSetting.fingerprintMode === "network" && defaultSetting.fingerprintDeviceId ? String(defaultSetting.fingerprintDeviceId) : null;

    const options: DeviceOption[] = [{
        value: USB_VALUE, id: null, kind: "usb", online: true, location: null, isDefault: usbDefault, group: "USB",
        label: "USB 등록기 (이 PC)" + (usbDefault ? " (지점 기본)" : "")
    }];

    const devices: any[] = info && Array.isArray(info.devices) ? info.devices.filter((each: any) => each.hasFingerprint) : [];
    const hasBranch = !!teacherLocation && teacherLocation !== "total";

    const groupOf = (each: any) => {
        if (hasBranch && each.location === teacherLocation) {
            return "내 지점";
        }
        return each.location ? (englishLocationToKorean(each.location) || each.location) : "기타";
    };

    const sortKey = (each: any) => {
        if (hasBranch && each.location === teacherLocation) return "0";
        if (each.location) return "1" + groupOf(each);
        return "2기타";
    };

    const sorted = [...devices].sort((a: any, b: any) => {
        const ak = sortKey(a);
        const bk = sortKey(b);
        if (ak !== bk) return ak.localeCompare(bk);
        return String(a.name).localeCompare(String(b.name));
    });

    sorted.forEach((each: any) => {
        const isDefault = defaultDeviceId !== null && String(each.id) === defaultDeviceId;
        options.push({
            value: String(each.id), id: String(each.id), kind: "network", online: +each.status === 1, location: each.location || null, isDefault,
            label: (each.name || each.id) + (isDefault ? " (지점 기본)" : ""),
            group: groupOf(each)
        });
    });

    return options;

};

const bannerColors: any = {
    danger: { bg: "#fef2f2", fg: "#b91c1c", border: "#fecaca" },
    warning: { bg: "#fffbeb", fg: "#b45309", border: "#fde68a" },
    success: { bg: "#eef2ff", fg: "#3730a3", border: "#c7d2fe" },
    neutral: { bg: "#f3f4f6", fg: "#4b5563", border: "#e5e7eb" },
};

const Banner: React.FC<any> = ({ color, style, children }) => {
    const c = bannerColors[color] || bannerColors.neutral;
    return (
        <div style={{ background: c.bg, color: c.fg, border: `1px solid ${c.border}`, borderRadius: "8px", padding: "9px 12px", fontSize: "13px", fontFamily: "Apple_SB", marginBottom: "10px", ...style }}>
            {children}
        </div>
    );
};

const Thumb: React.FC<{ src: string | null }> = ({ src }) => {
    if (src) {
        return <img src={`data:image/jpeg;base64,${src}`} alt="fingerprint" style={{ width: "44px", height: "58px", objectFit: "cover", borderRadius: "6px", border: "1px solid #e5e7eb", background: "#fff" }} />;
    }
    return (
        <div style={{ width: "44px", height: "58px", borderRadius: "6px", border: "1px dashed #d1d5db", fontSize: "10px", color: "#9ca3af", display: "flex", alignItems: "center", justifyContent: "center" }}>
            없음
        </div>
    );
};

interface ScanCardProps {
    number: number;
    title: string;
    status: "idle" | "scanning" | "verifying" | "done" | "error";
    disabled: boolean;            //두 번째 카드: 첫 스캔 전
    disabledHint?: string;
    idleHint: string;
    isUsb: boolean;
    secondsLeft: number;
    result: ScanResult | null;
    error: ScanError | null;
    locked: boolean;              //다른 작업 진행 중 / 장치 미선택 / 오프라인 → 버튼 비활성
    onScan: () => void;
}

const cardColors: any = {
    idle: { border: "#3b5bdb", bg: "#ffffff" },
    scanning: { border: "#3b5bdb", bg: "#ffffff" },
    verifying: { border: "#3b5bdb", bg: "#ffffff" },
    done: { border: "#86efac", bg: "#f0fdf4" },
    error: { border: "#fca5a5", bg: "#fef2f2" },
};

const ScanCard: React.FC<ScanCardProps> = (p) => {

    const c = p.disabled ? { border: "#e5e7eb", bg: "#f9fafb" } : cardColors[p.status];

    //카드 머리: 번호 동그라미 (완료 ✓ 초록 / 오류 ! 빨강 / 비활성 회색 / 그 외 파랑)
    const badgeBg = p.disabled ? "#e5e7eb" : p.status === "done" ? "#16a34a" : p.status === "error" ? "#dc2626" : "#3b5bdb";
    const badgeText = p.status === "done" && !p.disabled ? "✓" : p.status === "error" && !p.disabled ? "!" : String(p.number);
    const titleColor = p.disabled ? "#9ca3af" : p.status === "done" ? "#15803d" : p.status === "error" ? "#b91c1c" : "#111827";

    return (
        <div style={{ flex: 1, minHeight: "150px", border: `1.5px ${p.disabled ? "dashed" : "solid"} ${c.border}`, background: c.bg, borderRadius: "12px", padding: "14px 16px", display: "flex", flexDirection: "column" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                <div style={{ width: "22px", height: "22px", borderRadius: "50%", background: badgeBg, color: p.disabled ? "#6b7280" : "#fff", fontSize: "12px", fontFamily: "Apple_B", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {badgeText}
                </div>
                <div style={{ fontFamily: "Apple_B", fontSize: "14px", color: titleColor }}>{p.title}</div>
            </div>

            {p.disabled &&
                <div style={{ fontSize: "12px", color: "#9ca3af", flex: 1 }}>{p.disabledHint}</div>
            }

            {!p.disabled && p.status === "idle" &&
                <>
                    <div style={{ fontSize: "12px", color: "#4b5563", flex: 1, lineHeight: 1.5 }}>{p.idleHint}</div>
                    <Button size="sm" variant="solid" fullWidth sx={{ marginTop: "10px" }} onClick={p.onScan} disabled={p.locked}>스캔</Button>
                </>
            }

            {!p.disabled && p.status === "scanning" && (p.isUsb
                ?
                <>
                    <div style={{ fontSize: "12px", color: "#4b5563", flex: 1, lineHeight: 1.5 }}>USB 등록기 창이 열립니다. 그 창의 안내에 따라 손가락을 올려주세요.</div>
                    <LinearProgress sx={{ marginTop: "10px" }} />
                    <div style={{ fontSize: "11px", color: "#6b7280", marginTop: "6px" }}>스캔 대기 중…</div>
                </>
                :
                <>
                    <div style={{ fontSize: "12px", color: "#4b5563", flex: 1, lineHeight: 1.5 }}>장치에 {p.number === 1 ? "손가락을 올려주세요." : "같은 손가락을 다시 올려주세요."}</div>
                    <LinearProgress determinate value={(NETWORK_SCAN_SECONDS - p.secondsLeft) / NETWORK_SCAN_SECONDS * 100} sx={{ marginTop: "10px" }} />
                    <div style={{ fontSize: "11px", color: "#6b7280", marginTop: "6px" }}>{p.secondsLeft > 0 ? `${p.secondsLeft}초 남음` : "응답 대기 중…"}</div>
                </>
            )}

            {!p.disabled && p.status === "verifying" &&
                <>
                    <div style={{ fontSize: "12px", color: "#4b5563", flex: 1, lineHeight: 1.5 }}>두 지문을 대조하고 있습니다.</div>
                    <LinearProgress sx={{ marginTop: "10px" }} />
                    <div style={{ fontSize: "11px", color: "#6b7280", marginTop: "6px" }}>지문 대조 중…</div>
                </>
            }

            {!p.disabled && p.status === "done" &&
                <>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px", flex: 1 }}>
                        <Thumb src={p.result ? p.result.image : null} />
                        <div style={{ fontFamily: "Apple_SB", fontSize: "13px", color: "#15803d" }}>
                            완료{p.result && p.result.quality !== null ? ` · 품질 ${p.result.quality}` : ""}
                        </div>
                    </div>
                    <Button size="sm" variant="outlined" color="neutral" fullWidth sx={{ marginTop: "10px", backgroundColor: "#fff" }} onClick={p.onScan} disabled={p.locked}>다시 스캔</Button>
                </>
            }

            {!p.disabled && p.status === "error" &&
                <>
                    <div style={{ flex: 1 }}>
                        <div style={{ fontFamily: "Apple_SB", fontSize: "13px", color: "#b91c1c", lineHeight: 1.5 }}>{p.error ? p.error.message : "스캔에 실패했습니다."}</div>
                        <div style={{ fontSize: "11px", color: "#9ca3af", marginTop: "2px" }}>{p.error ? p.error.code : ""}</div>
                    </div>
                    <Button size="sm" variant="outlined" color="danger" fullWidth sx={{ marginTop: "10px", backgroundColor: "#fff" }} onClick={p.onScan} disabled={p.locked}>다시 스캔</Button>
                </>
            }
        </div>
    );
};

const FingerprintRecognitionModal: React.FC<FingerprintRecognitionModalProps> = (props) => {

    const [infoLoading, setInfoLoading] = useState(false);
    const [info, setInfo] = useState<any>(null);          // /fingerprint/info 응답 통째로
    const [infoError, setInfoError] = useState("");

    const [selectedDevice, setSelectedDevice] = useState("");
    const [quality, setQuality] = useState(80);

    const [phase, setPhase] = useState<Phase>("idle");
    const [scan1, setScan1] = useState<ScanResult | null>(null);
    const [scan2, setScan2] = useState<ScanResult | null>(null);
    const [error1, setError1] = useState<ScanError | null>(null);
    const [error2, setError2] = useState<ScanError | null>(null);
    const [secondsLeft, setSecondsLeft] = useState(NETWORK_SCAN_SECONDS);

    const failCountRef = useRef(0);                          //두 번째 스캔 연속 실패 횟수
    const countdownRef = useRef<any>(null);                  //setInterval id
    const abortRef = useRef<AbortController | null>(null);   //USB 에이전트 fetch abort
    const openSeqRef = useRef(0);                            //닫힌 뒤 늦게 도착한 응답 무시용 시퀀스

    //---- 파생값 ----
    const teacherLocation: string | null = info ? info.teacherLocation : null;
    const hasTeacherBranch = !!teacherLocation && teacherLocation !== "total";
    const defaultInfo = resolveDefaultSetting(info);
    const deviceOptions = buildDeviceOptions(info, teacherLocation, defaultInfo.setting);
    const selectedOption = deviceOptions.find((each) => each.value === selectedDevice);
    const isUsb = !!selectedOption && selectedOption.kind === "usb";
    const deviceOffline = !!selectedOption && selectedOption.kind === "network" && !selectedOption.online;
    const noSetting = !!info && hasTeacherBranch && !defaultInfo.setting;
    const noDevices = !!info && (!Array.isArray(info.devices) || info.devices.length === 0);

    const busy = phase === "scanning1" || phase === "scanning2" || phase === "verifying" || phase === "enrolling";
    const canScan = !!info && !infoLoading && !!selectedOption && !deviceOffline && !busy;
    //스캔 중 + 첫 스캔 결과가 있는 동안 장치/품질 잠금 (두 템플릿과 verify 가 같은 장치로 이뤄지게)
    const selectorsLocked = busy || scan1 !== null;

    const card1Status: ScanCardProps["status"] = phase === "scanning1" ? "scanning" : phase === "error1" ? "error" : scan1 ? "done" : "idle";
    const card2Status: ScanCardProps["status"] = phase === "scanning2" ? "scanning" : phase === "verifying" ? "verifying"
        : phase === "error2" ? "error" : (phase === "done2" || phase === "enrolling") ? "done" : "idle";
    const card2Disabled = !scan1 || phase === "scanning1" || phase === "error1";

    const fingerprints: any[] = info && info.biostar && Array.isArray(info.biostar.fingerprints) ? info.biostar.fingerprints : [];

    //---- 타이머 / 리셋 ----
    const stopCountdown = () => {
        if (countdownRef.current) {
            clearInterval(countdownRef.current);
            countdownRef.current = null;
        }
    };

    const startCountdown = () => {
        stopCountdown();
        setSecondsLeft(NETWORK_SCAN_SECONDS);
        //0에서 멈추고 "응답 대기 중…" 표시. 실제 종료는 서버 응답(최대 15초 블로킹)이 결정
        countdownRef.current = setInterval(() => {
            setSecondsLeft((prev) => (prev <= 1 ? 0 : prev - 1));
        }, 1000);
    };

    const resetAll = () => {
        stopCountdown();
        setPhase("idle");
        setScan1(null);
        setScan2(null);
        setError1(null);
        setError2(null);
        failCountRef.current = 0;
    };

    const closeModal = () => {
        props.setOpen(false);   //실제 정리는 아래 props.open === false 효과에서
    };

    useEffect(() => {

        if (!props.open) {
            //닫힘 (X / 취소 / 백드롭 / 부모) — 진행 중이던 스캔·verify·enroll 응답 무시 + 정리
            openSeqRef.current += 1;
            if (abortRef.current) {
                abortRef.current.abort();
            }
            resetAll();
            setInfo(null);
            setInfoError("");
            setSelectedDevice("");
            setQuality(80);
            return;
        }

        if (!props.selectedUserId) {
            return;
        }

        loadInfo();

    }, [props.open, props.selectedUserId]);

    //언마운트 시 타이머/USB 요청 정리
    useEffect(() => {
        return () => {
            stopCountdown();
            if (abortRef.current) {
                abortRef.current.abort();
            }
        };
    }, []);

    const mapInfoError = (result: any) => {
        if (result.message === "noFingerprintId") return "지문인식ID가 없는 학생입니다. 지문인식ID를 먼저 연결해주세요.";
        if (result.message === "error" && result.error === "user_not_found") return "BioStar에서 이 학생(지문인식ID)을 찾을 수 없습니다. 지문인식ID를 확인해주세요.";
        if (result.message === "error" && result.error === "biostar_error") return "BioStar 서버와 통신에 실패했습니다. 잠시 후 다시 시도해주세요.";
        return "지문 정보를 불러오지 못했습니다.";
    };

    const loadInfo = async () => {

        const seq = openSeqRef.current;

        setInfoLoading(true);
        setInfoError("");

        try {
            const result: any = await getFetch(`${API}/fingerprint/info?studentId=${props.selectedUserId}`);

            if (seq !== openSeqRef.current) return;

            if (result.message === "success") {
                setInfo(result);
                //기본 장치 자동 선택
                const d = resolveDefaultSetting(result);
                const options = buildDeviceOptions(result, result.teacherLocation, d.setting);
                const found = options.find((each) => each.isDefault);
                setSelectedDevice(found ? found.value : "");
            } else {
                setInfoError(mapInfoError(result));
            }
        } catch (e) {
            console.log(e);
            if (seq === openSeqRef.current) {
                setInfoError("서버와 통신에 실패했습니다.");
            }
        } finally {
            if (seq === openSeqRef.current) {
                setInfoLoading(false);
            }
        }

    };

    //---- USB 에이전트 (로컬 fetch, 이 파일의 유일한 직접 fetch) ----
    const usbFetch = async (path: string, init: any) => {

        const controller = new AbortController();
        abortRef.current = controller;
        const timeoutId = setTimeout(() => controller.abort(), USB_TIMEOUT_MS);

        try {
            var lastError: any = null;
            for (const base of USB_AGENT_BASES) {
                try {
                    const response = await fetch(base + path, { ...init, credentials: "omit", signal: controller.signal });
                    return await response.json();
                } catch (e) {
                    lastError = e;
                    if (controller.signal.aborted) {
                        throw e;   //타임아웃/닫힘이면 폴백 시도 안 함
                    }
                    //TLS/연결 실패 → 다음 base(http 8082)로 폴백
                }
            }
            throw lastError;
        } finally {
            clearTimeout(timeoutId);
            abortRef.current = null;
        }

    };

    const scanViaUsbAgent = async (): Promise<ScanOutcome> => {
        try {
            const result: any = await usbFetch(`/api/ScanBiomini?enroll_quality=${quality}&template_type=2001&timestamp=${Date.now()}`, { method: "GET" });

            if (result && result.retcode === 1 && result.template) {
                return {
                    ok: true,
                    data: {
                        template: String(result.template),
                        quality: typeof result.score === "number" ? result.score : null,
                        image: result.image && result.image.data ? result.image.data : null
                    }
                };
            }

            return { ok: false, error: { message: "스캔이 취소되었거나 실패했습니다.", code: `retcode ${result ? result.retcode : "?"}` } };
        } catch (e: any) {
            console.log(e);
            return { ok: false, error: e && e.name === "AbortError" ? AGENT_TIMEOUT : AGENT_UNREACHABLE };
        }
    };

    //null = 대조 자체 실패 (에이전트 오류)
    const verifyViaUsbAgent = async (a: string, b: string): Promise<boolean | null> => {
        try {
            const body = `template1=${encodeURIComponent(a)}&template2=${encodeURIComponent(b)}&template_type=2001&timestamp=${Date.now()}`;
            const result: any = await usbFetch("/api/Verify", {
                method: "POST",
                headers: { "Content-Type": "application/x-www-form-urlencoded" },
                body
            });
            if (!result || typeof result.retcode !== "number") return null;
            return result.retcode === 1;
        } catch (e) {
            console.log(e);
            return null;
        }
    };

    //---- 네트워크 장치 (서버 경유) ----
    const scanViaNetwork = async (deviceId: string): Promise<ScanOutcome> => {
        try {
            startCountdown();
            //서버가 최대 15초 블로킹, postFetch 타임아웃 30초라 충분
            const result: any = await postFetch(`${API}/fingerprint/scan`, { deviceId, quality });

            if (result.message === "success") {
                if (!result.template) {
                    return { ok: false, error: EMPTY_TEMPLATE };   //시연용 read-only 계정 등 가짜 success 방어
                }
                return {
                    ok: true,
                    data: {
                        template: String(result.template),
                        quality: typeof result.quality === "number" ? result.quality : null,
                        image: result.image || null
                    }
                };
            }

            return { ok: false, error: mapServerScanError(result.error || result.message) };
        } catch (e) {
            console.log(e);
            return { ok: false, error: SERVER_UNREACHABLE };
        } finally {
            stopCountdown();
        }
    };

    const verifyViaNetwork = async (deviceId: string, a: string, b: string): Promise<boolean | null> => {
        try {
            const result: any = await postFetch(`${API}/fingerprint/verify`, { deviceId, template0: a, template1: b });
            if (result.message !== "success" || typeof result.match !== "boolean") return null;
            return result.match;
        } catch (e) {
            console.log(e);
            return null;
        }
    };

    //서버가 device_offline 을 돌려주면 목록의 해당 장치도 오프라인으로 바꿔 배너/비활성 반영
    const markSelectedDeviceOffline = () => {
        setInfo((prev: any) => {
            if (!prev || !Array.isArray(prev.devices)) return prev;
            return { ...prev, devices: prev.devices.map((each: any) => String(each.id) === selectedDevice ? { ...each, status: 0 } : each) };
        });
    };

    const failSecondScan = (error: ScanError) => {
        failCountRef.current += 1;
        if (failCountRef.current >= MAX_CONSECUTIVE_FAIL) {
            resetAll();
            notifyError("연속 3회 실패하여 처음부터 다시 스캔합니다.");
            return;
        }
        setScan2(null);
        setError2(error);
        setPhase("error2");
    };

    //---- 공통 흐름 ----
    const scanFingerprint = async (slot: 1 | 2) => {

        if (!canScan || !selectedOption) return;

        const seq = openSeqRef.current;

        if (slot === 1) {
            //첫 스캔(다시 스캔 포함)은 두 번째 결과도 함께 버림
            setScan1(null);
            setScan2(null);
            setError1(null);
            setError2(null);
            setPhase("scanning1");
        } else {
            setScan2(null);
            setError2(null);
            setPhase("scanning2");
        }

        const outcome = selectedOption.kind === "usb" ? await scanViaUsbAgent() : await scanViaNetwork(selectedOption.id as string);

        if (seq !== openSeqRef.current) return;   //모달이 닫힘

        if (!outcome.ok) {
            if (outcome.error.code === "device_offline") {
                markSelectedDeviceOffline();
            }
            if (slot === 1) {
                setError1(outcome.error);
                setPhase("error1");
                return;
            }
            failSecondScan(outcome.error);
            return;
        }

        failCountRef.current = 0;

        if (slot === 1) {
            setScan1(outcome.data);
            setPhase("done1");
            return;
        }

        setScan2(outcome.data);
        setPhase("verifying");
        await verifyFingerprints(scan1 as ScanResult, outcome.data, seq);

    };

    const verifyFingerprints = async (first: ScanResult, second: ScanResult, seq: number) => {

        if (!selectedOption) return;

        const match = selectedOption.kind === "usb"
            ? await verifyViaUsbAgent(first.template, second.template)
            : await verifyViaNetwork(selectedOption.id as string, first.template, second.template);

        if (seq !== openSeqRef.current) return;

        if (match === null) {
            failSecondScan(VERIFY_FAILED);   //대조 자체 실패 → 두 번째 스캔 실패로 취급
            return;
        }

        if (match) {
            setPhase("done2");
            return;
        }

        setPhase("mismatch");

    };

    const enroll = async () => {

        if (phase !== "done2" || !scan1 || !scan2 || !selectedOption || !props.selectedUserId) return;

        const seq = openSeqRef.current;
        setPhase("enrolling");

        try {
            const body: any = {
                studentId: props.selectedUserId,
                template0: scan1.template,
                template1: scan2.template,
                mode: selectedOption.kind,
                quality
            };
            if (selectedOption.kind === "network") {
                body.deviceId = selectedOption.id;
            }

            const result: any = await postFetch(`${API}/fingerprint/enroll`, body);

            if (seq !== openSeqRef.current) return;

            if (result.message === "success") {
                notifySuccess("지문이 등록되었습니다.");
                props.onEnrolled(typeof result.fingerprintCount === "number" ? result.fingerprintCount : fingerprints.length + 1);
                closeModal();
                return;
            }

            if (result.message === "error" && result.error === "user_busy") {
                notifyError("같은 학생의 지문 등록이 진행 중입니다. 잠시 후 다시 시도해주세요.");
            } else if (result.message === "error" && result.error === "user_not_found") {
                notifyError("BioStar에서 이 학생을 찾을 수 없습니다. 지문인식ID를 확인해주세요.");
            } else if (result.message === "NOT_AUTHORIZED") {
                notifyError("권한이 없습니다.");
            } else {
                notifyError("지문 등록에 실패했습니다. 다시 시도해주세요.");
            }
            setPhase("done2");   //템플릿은 유지 → 등록 재시도 가능
        } catch (e) {
            console.log(e);
            if (seq === openSeqRef.current) {
                notifyError("등록 중 오류가 발생했습니다.");
                setPhase("done2");
            }
        }

    };

    //---- 헤더 표시값 ----
    const headerName = (info && info.student && info.student.name) || props.selectedUserName || "-";
    const headerLocation = (info && info.student && info.student.location) || props.selectedUserLocation || "지점 미입력";
    const headerFingerprintId = (info && info.biostar && info.biostar.user_id) || props.selectedUserFingerprintId || "-";

    return (
        <Modal open={props.open} onClose={closeModal}>
            <ModalDialog sx={{ padding: "22px 26px", maxWidth: "94vw" }}>
                <ModalClose />
                <div style={{ width: "580px", maxWidth: "88vw", maxHeight: "82vh", overflowY: "auto", fontFamily: "Apple_R", paddingRight: "4px" }}>

                    {/* 헤더: 학생명 · 지점 · 지문인식ID */}
                    <div style={{ fontFamily: "Apple_B", fontSize: "19px" }}>지문 등록</div>
                    <div style={{ fontSize: "13px", color: "#6b7280", marginTop: "4px", marginBottom: "18px" }}>
                        {headerName} · {headerLocation} · 지문인식ID {headerFingerprintId}
                    </div>

                    {infoLoading &&
                        <div style={{ display: "flex", justifyContent: "center", padding: "40px 0" }}>
                            <CircularProgress />
                        </div>
                    }

                    {!infoLoading && infoError &&
                        <Banner color="danger">{infoError}</Banner>
                    }

                    {!infoLoading && info &&
                        <>
                            {/* 상단 배너 */}
                            {deviceOffline && selectedOption &&
                                <Banner color="danger">{selectedOption.label}이(가) 오프라인입니다. 다른 장치를 선택하거나 관리자에게 문의하세요.</Banner>
                            }
                            {noSetting &&
                                <Banner color="warning">관리자 설정에서 장치를 먼저 지정하세요. (본사직원페이지 &gt; 지점별 인증 등록장치 설정) 지정 전에는 아래에서 장치를 직접 선택할 수 있습니다.</Banner>
                            }
                            {!hasTeacherBranch && !defaultInfo.setting &&
                                <Banner color="neutral">소속 지점이 없는 계정이라 지점 기본 장치가 없습니다. 장치를 직접 선택하세요.</Banner>
                            }
                            {noDevices &&
                                <Banner color="warning">BioStar 장치 목록을 불러오지 못했습니다. USB 등록기만 사용할 수 있습니다.</Banner>
                            }

                            {/* Row 1: 장치 + 품질 */}
                            <div style={{ display: "flex", alignItems: "flex-end", gap: "14px", marginBottom: "18px" }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontFamily: "Apple_SB", fontSize: "12px", color: "#4b5563", marginBottom: "6px" }}>장치</div>
                                    <Select
                                        size="sm"
                                        placeholder="장치를 선택하세요"
                                        value={selectedDevice ? selectedDevice : null}
                                        disabled={selectorsLocked}
                                        onChange={(e: any, value: any) => {
                                            if (value !== null && !String(value).startsWith("__")) {
                                                setSelectedDevice(String(value));
                                            }
                                        }}
                                    >
                                        {deviceOptions.map((each, index) => {
                                            const showHeader = index === 0 || deviceOptions[index - 1].group !== each.group;
                                            return (
                                                <React.Fragment key={each.value}>
                                                    {showHeader &&
                                                        <Option value={"__" + each.group} disabled sx={{ fontSize: "11px", color: "#9ca3af", minHeight: "24px" }}>{each.group}</Option>
                                                    }
                                                    {/* 오프라인은 선택 불가(회색). 단, 현재 선택된(기본) 장치는 값 표시를 위해 활성 유지 → 배너가 안내 */}
                                                    <Option value={each.value} disabled={!each.online && each.value !== selectedDevice} sx={{ color: each.online ? undefined : "#9ca3af" }}>
                                                        {each.online ? "● " : "○ "}{each.label}{each.online ? "" : " · 오프라인"}
                                                    </Option>
                                                </React.Fragment>
                                            );
                                        })}
                                    </Select>
                                </div>
                                <div>
                                    <div style={{ fontFamily: "Apple_SB", fontSize: "12px", color: "#4b5563", marginBottom: "6px" }}>품질</div>
                                    <ToggleButtonGroup
                                        size="sm"
                                        value={String(quality)}
                                        onChange={(e: any, value: any) => {
                                            if (value !== null) {
                                                setQuality(+value);
                                            }
                                        }}
                                    >
                                        {QUALITY_OPTIONS.map((q) => (
                                            <Button key={q} value={String(q)} disabled={selectorsLocked} sx={{ minWidth: "48px" }}>{q}</Button>
                                        ))}
                                    </ToggleButtonGroup>
                                </div>
                            </div>

                            {/* 등록된 지문 */}
                            <div style={{ fontFamily: "Apple_SB", fontSize: "14px", marginBottom: "8px" }}>
                                등록된 지문 <span style={{ fontFamily: "Apple_R", fontSize: "12px", color: "#6b7280" }}>{fingerprints.length}개</span>
                            </div>
                            {fingerprints.length === 0
                                ?
                                <div style={{ fontSize: "12px", color: "#9ca3af", marginBottom: "18px" }}>등록된 지문이 없습니다.</div>
                                :
                                <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", marginBottom: "18px" }}>
                                    {fingerprints.map((each: any, index: number) => (
                                        <div key={index} style={{ border: "1px solid #e5e7eb", borderRadius: "10px", padding: "8px 10px", display: "flex", gap: "6px", alignItems: "center", background: "#fafafa" }}>
                                            <Thumb src={each.image0} />
                                            <Thumb src={each.image1} />
                                            <div style={{ marginLeft: "6px" }}>
                                                <div style={{ fontFamily: "Apple_SB", fontSize: "13px" }}>{index + 1}번째</div>
                                                <div style={{ fontSize: "11px", color: "#9ca3af", marginTop: "2px" }}>{each.enrollDate ? `${each.enrollDate} 등록` : "—"}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            }

                            {/* 새 지문 추가 */}
                            <div style={{ fontFamily: "Apple_SB", fontSize: "14px", marginBottom: "10px" }}>
                                새 지문 추가 <span style={{ fontFamily: "Apple_R", fontSize: "12px", color: "#6b7280" }}>같은 손가락을 두 번 스캔합니다</span>
                            </div>

                            {phase === "mismatch"
                                ?
                                <div style={{ border: "1.5px solid #fca5a5", background: "#fef2f2", borderRadius: "12px", padding: "18px 20px" }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                        <div style={{ width: "22px", height: "22px", borderRadius: "50%", background: "#dc2626", color: "#fff", fontSize: "13px", display: "flex", alignItems: "center", justifyContent: "center" }}>⇄</div>
                                        <div style={{ fontFamily: "Apple_B", color: "#b91c1c", fontSize: "15px" }}>두 지문이 일치하지 않습니다</div>
                                    </div>
                                    <div style={{ fontSize: "13px", color: "#b91c1c", marginTop: "8px" }}>같은 손가락으로 처음부터 다시 스캔해주세요.</div>
                                    <Button size="sm" variant="outlined" color="danger" sx={{ marginTop: "12px", backgroundColor: "#fff" }} onClick={resetAll}>처음부터 다시</Button>
                                </div>
                                :
                                <div style={{ display: "flex", gap: "12px" }}>
                                    <ScanCard
                                        number={1}
                                        title="첫 번째 스캔"
                                        status={card1Status}
                                        disabled={false}
                                        idleHint={isUsb ? "스캔 버튼을 누르면 USB 등록기 창이 열립니다." : "스캔 버튼을 누른 뒤 장치에 손가락을 올려주세요."}
                                        isUsb={isUsb}
                                        secondsLeft={secondsLeft}
                                        result={scan1}
                                        error={error1}
                                        locked={!canScan}
                                        onScan={() => scanFingerprint(1)}
                                    />
                                    <ScanCard
                                        number={2}
                                        title="두 번째 스캔"
                                        status={card2Status}
                                        disabled={card2Disabled}
                                        disabledHint="첫 번째 스캔 완료 후 활성화됩니다."
                                        idleHint={isUsb ? "같은 손가락을 USB 등록기에 한 번 더 올립니다." : "같은 손가락을 장치에 한 번 더 올립니다."}
                                        isUsb={isUsb}
                                        secondsLeft={secondsLeft}
                                        result={scan2}
                                        error={error2}
                                        locked={!canScan}
                                        onScan={() => scanFingerprint(2)}
                                    />
                                </div>
                            }

                            {(phase === "done2" || phase === "enrolling") &&
                                <Banner color="success" style={{ marginTop: "12px", marginBottom: 0 }}>✓ 두 지문이 일치합니다. 등록을 누르면 저장됩니다.</Banner>
                            }
                        </>
                    }

                    {/* 푸터 */}
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "22px" }}>
                        <Button variant="outlined" color="neutral" onClick={closeModal}>취소</Button>
                        <Button variant="solid" onClick={enroll} disabled={phase !== "done2" && phase !== "enrolling"} loading={phase === "enrolling"}>등록</Button>
                    </div>

                </div>
            </ModalDialog>
        </Modal>
    );

};

export default FingerprintRecognitionModal;
